<template>
  <div class="page-container">
    <div :class="{ 'upload-data': uploadData }" class="update-container update-analitics-target">
      <circle-loader v-if="uploadData" size="xlarge" :indeterminate="true" />

      <div v-if="editObject !== null" class="content-wrapper">
        <div class="row">
          <div class="col-6">
            <div class="row">
              <div class="col-12">
                <form-data
                  label="Наименование"
                  placeholder="Наименование целевого показателя"
                  fieldname="editObject.name"
                  :input-model="editObject.name"
                  :error="validationErrors.name"
                  :error-info="errorsInfos.name ? errorsInfos.name : ''"
                  :server-error="serverErrors.name"
                  @update="updateData"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-6">
                <form-select
                  :input-model="editObject.unit"
                  fieldname="editObject.unit"
                  label="Единица измерения"
                  :input-options="itemUnitsAsOptions"
                  :error="validationErrors.unit"
                  :error-info="errorsInfos.unit ? errorsInfos.unit : ''"
                  :server-error="serverErrors.unit"
                  @update="updateData"
                />
              </div>
              <div class="col-6">
                <form-simple-date
                  label="Период действия"
                  placeholder="Период действия"
                  fieldname="editObject.years_period"
                  :input-model="editObject.years_period"
                  :error="validationErrors.years_period"
                  :error-info="errorsInfos.years_period ? errorsInfos.years_period : ''"
                  :server-error="serverErrors.years_period"
                  type="year"
                  format="YYYY"
                  range-separator="-"
                  :range="true"
                  :confirm="true"
                  :shortcuts="false"
                  :not-after="upperYearPeriodLimit"
                  :not-before="lowerYearPeriodLimit"
                  @update="updateData"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <form-data
                  label="Источник"
                  fieldname="editObject.source"
                  :input-model="editObject.source"
                  :error="validationErrors.source"
                  :error-info="errorsInfos.source ? errorsInfos.source : ''"
                  :server-error="serverErrors.source"
                  tag-type="textarea"
                  @update="updateData"
                />
              </div>
            </div>
          </div>
          <div v-if="editObject.years_period[0] !== null || editObject.years_period[1] !== null" class="col-6">
            <chart :charts-data="chartsData" :categories="categories" :yaxis-title="yaxisTitle" />
          </div>
        </div>
        <div v-if="editObject.years_period[0] !== null || editObject.years_period[1] !== null" class="row">
          <div class="col-12">
            <h3 class="marge">План-фактные значения</h3>
            <fill-value-table
              header-title="Показатель"
              :header-values="getYearPeriodsForFillValueTable()"
              :body-titles="['План', 'Факт']"
              :body-values="getIndicationsForFillValueTable()"
              @change="onFillValueTableChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import moment from "moment";
import isEquil from "lodash.isequal";

import FormData from "../../../../../components/forms/FormData";
import FormSimpleDate from "../../../../../components/forms/FormSimpleDate";
import FormSelect from "../../../../../components/forms/FormSelect";
import FillValueTable from "../../../../../components/FillValueTable";
import CircleLoader from "../../../../../components/CircleLoader";

import Chart from "../common/Chart";

export default {
  name: "UpdateAnaliticsTarget",
  components: {
    FormData,
    FormSimpleDate,
    FormSelect,
    FillValueTable,
    Chart,
    CircleLoader
  },
  data() {
    return {
      upperYearPeriodLimit: moment()
        .add(20, "years")
        .toDate(),
      lowerYearPeriodLimit: moment("01.01.2010", "DD.MM.YYYY").toDate(),
      chartsData: [
        {
          name: "Planned",
          data: []
        },
        {
          name: "Actual",
          data: []
        }
      ],
      categories: [],
      yaxisTitle: ""
    };
  },
  computed: {
    ...mapState("analiticsTargets", [
      "itemUnits",
      "uploadData",
      "editObject",
      "validationErrors",
      "serverErrors",
      "errorsInfos",
      "fieldRequires",
      "editMode"
    ]),
    ...mapGetters("analiticsTargets", ["itemUnitsAsOptions"])
  },
  watch: {
    "editObject.years_period"(newValue, oldValue) {
      if (!Array.isArray(newValue) && isEquil(newValue, oldValue)) return;

      if (newValue && newValue[0] !== null && newValue[1] !== null) {
        this.onFillValueTableChange(this.getIndicationsForFillValueTable());
      }
    },
    "editObject.unit"() {
      this.yaxisTitle = this.itemUnits[this.editObject.unit];
    }
  },
  mounted() {
    const { objectId } = this.$route.params;

    if (!this.editMode) {
      this.setUploadData(true);
      this.startEditing(objectId).then(() => {
        this.resetValidationErrorsObject();
        this.setEditMode(true);
        this.setUploadData(false);

        this.categories =
          this.editObject &&
          this.editObject.years_period &&
          (this.editObject.years_period[0] !== null || this.editObject.years_period[1] !== null)
            ? this.getYearPeriodsForFillValueTable().map(year => year.replace(" год", ""))
            : [];

        this.yaxisTitle = this.itemUnits[this.editObject.unit];
      });
    }
  },
  beforeDestroy() {
    this.setEditMode(false);
  },
  methods: {
    ...mapActions(["getSource"]),
    ...mapActions("analiticsTargets", ["addNewObject", "updateObject", "startEditing"]),
    ...mapMutations("analiticsTargets", [
      "updateEditObjectField",
      "updateValidationErrorsObject",
      "updateValidationErrorsField",
      "updateServerErrorsField",
      "resetValidationErrorsObject",
      "setUploadData",
      "setEditMode"
    ]),
    updateData(data) {
      const target = data.fieldname.split(".");

      if (data.fieldname.includes("years_period")) {
        this.updateValidationErrorsField({ fieldName: target[1], newFieldValue: false });
        this.updateServerErrorsField({ fieldName: target[1], newFieldValue: false });
        this.updateEditObjectField({
          fieldName: target[1],
          newFieldValue: [
            data.value[0] ? moment(data.value[0]) : data.value[0],
            data.value[1] ? moment(data.value[1]) : data.value[1]
          ]
        });
        // Update chart's categories list
        if (data.value[0] === null || data.value[1] === null) {
          this.categories = [];
        } else {
          this.categories = this.getYearPeriodsForFillValueTable().map(year => year.replace(" год", ""));
        }
        return;
      }

      if (Array.isArray(target) && target.length > 0) {
        this.updateValidationErrorsField({ fieldName: target[1], newFieldValue: false });
        this.updateServerErrorsField({ fieldName: target[1], newFieldValue: false });
        this.updateEditObjectField({ fieldName: target[1], newFieldValue: data.value === null ? null : data.value });
      }
    },
    getYearPeriodsForFillValueTable() {
      const periods = [];

      if (this.editObject.years_period && this.editObject.years_period[0] && this.editObject.years_period[1]) {
        for (let i = this.editObject.years_period[0].format("YYYY"); i <= this.editObject.years_period[1].format("YYYY"); i++) {
          periods.push(`${i} год`);
        }
      }

      return periods;
    },
    getIndicationsForFillValueTable() {
      const bodyValues = [this.editObject.planned_indications, this.editObject.actual_indications];
      const sliceIndex = this.editObject.years_period[1].format("YYYY") - this.editObject.years_period[0].format("YYYY") + 1;

      return bodyValues.map(item => {
        const newValues = Object.values(item).slice(0, sliceIndex);

        if (newValues.length < sliceIndex) {
          for (let i = newValues.length; i < sliceIndex; i++) {
            newValues.push(0);
          }
        }
        return newValues;
      });
    },
    onFillValueTableChange(data) {
      const yearsWithoutPrefix = this.getYearPeriodsForFillValueTable().map(year => year.replace(" год", ""));

      this.editObject.planned_indications = data[0].reduce((obj, item, index) => {
        obj[yearsWithoutPrefix[index]] = item;
        return obj;
      }, {});
      this.editObject.actual_indications = data[1].reduce((obj, item, index) => {
        obj[yearsWithoutPrefix[index]] = item;
        return obj;
      }, {});

      this.chartsData = [
        {
          name: "План",
          data: data[0]
        },
        {
          name: "Факт",
          data: data[1]
        }
      ];
    }
  }
};
</script>
